/*
 * Copyright (C) 2020 to Present Applause App Quality, Inc. All rights reserved.
 */

import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { AfterContentInit, Directive, ElementRef, HostBinding, Input, OnInit, Renderer2 } from '@angular/core';
import { NgfA11yService } from '@ng-falcon/ngf-a11y/ngf-a11y.service';
import { NgfButtonMode } from '@ng-falcon/button/ngf-button/ngf-button-mode';
import { NgfButtonSize } from '@ng-falcon/button/ngf-button/ngf-button-size';
import { NgfButtonVariant } from '@ng-falcon/button/ngf-button/ngf-button-variant';

@Directive({
  selector: 'button[ngfButton],a[ngfButton]',
  standalone: true
})
export class NgfButtonDirective implements OnInit, AfterContentInit {

  @HostBinding('attr.ngfButton')
  @Input('ngfButton')
  variant: NgfButtonVariant | '';

  @HostBinding('attr.size')
  @Input()
  size: NgfButtonSize | null = null;

  /**
   * ngfButton can detect being text or icon button by itself
   * in some rare cases it might incorrectly detect it, so mode can be forced
   */
  @HostBinding('attr.mode')
  @Input()
  mode: NgfButtonMode | null = null;

  @HostBinding('attr.active')
  @Input()
  active: boolean | null = null;

  /**
   * By default, rapid repeated clicks are debounced (500ms) to guard against
   * accidental double-submits. Set to false for controls meant to be clicked
   * repeatedly in quick succession, e.g. stepper +/- buttons.
   */
  @Input()
  debounceClicks = true;

  @Input()
  set passive(enabled: boolean | string) {
    enabled = coerceBooleanProperty(enabled);

    this.attr({ name: 'passive', value: '', enabled });
    this.attr({ name: 'aria-disabled', value: 'true', enabled });
    this.attr({ name: 'tabindex', value: '-1', enabled });

    if (this.elementRef.nativeElement instanceof HTMLAnchorElement) {
      // for passive state, we have to treat links as buttons,
      // this with combination of `aria-disabled` will allow screen readers
      // to recognize it as not interactive element,
      // and only read text
      this.attr({ name: 'role', value: 'button', enabled });
    }
  }

  constructor(readonly elementRef: ElementRef<HTMLElement>,
              private renderer: Renderer2,
              private a11y: NgfA11yService) { }

  ngOnInit(): void {
    this.a11y.prepareClickableElement(this.elementRef, this.renderer, { debounceClicks: this.debounceClicks });
  }

  ngAfterContentInit(): void {
    this.applyAnatomy();
  }

  private attr(config: { name: string, value: any, enabled: boolean }): void {
    if (config.enabled) {
      this.renderer.setAttribute(this.elementRef.nativeElement, config.name, String(config.value));
    } else {
      this.renderer.removeAttribute(this.elementRef.nativeElement, config.name);
    }
  }

  private applyAnatomy(): void {
    if (this.mode === 'icon') {
      this.renderer.addClass(this.elementRef.nativeElement, 'ngf-button--icon-button');
      return;
    }

    const children = this.findChildNodes();

    if (children.length === 0) {
      // nothing to do
      return;
    }

    if (children.length === 1) {
      if (this.isMatIcon(children[0])) {
        this.renderer.addClass(this.elementRef.nativeElement, 'ngf-button--icon-button');
      } else {
        this.renderer.addClass(this.elementRef.nativeElement, 'ngf-button--text-button');
      }
    } else {
      this.renderer.addClass(this.elementRef.nativeElement, 'ngf-button--text-button');

      if (this.isMatIcon(children[0])) {
        this.renderer.addClass(this.elementRef.nativeElement, 'ngf-button--with-left-icon');
      }

      if (this.isMatIcon(children[children.length - 1])) {
        this.renderer.addClass(this.elementRef.nativeElement, 'ngf-button--with-right-icon');
      }
    }
  }

  private findChildNodes(): readonly ChildNode[] {
    return Array.from(this.elementRef.nativeElement.childNodes)
      // removing text nodes with whitespaces only
      .filter(node => node instanceof Text ? node.data.trim() !== '' : true)
      // removing comment nodes
      .filter(node => !(node instanceof Comment));
  }

  private isMatIcon(node: ChildNode): node is HTMLElement {
    return node instanceof HTMLElement && node.tagName.toLowerCase() === 'mat-icon';
  }
}
